import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutDriver } from "../features/driver/driverAuthSlice";
import { updateDriverAvailability } from "../features/driver/driverSlice";
import {
  FaHome,
  FaCarAlt,
  FaUserAlt,
  FaSignOutAlt,
  FaHistory,
  FaBell,
  FaWallet,
  FaChevronDown,
  FaChevronRight,
  FaMoneyBillWave,
  FaClipboardList,
  FaPercentage,
} from "react-icons/fa";

const DriverLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingRidesCount, setPendingRidesCount] = useState(0);
  const [walletOpen, setWalletOpen] = useState(false);
  const hasNavigated = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { driver: driverUser } = useSelector((state) => state.driverAuth);
  const { driver: driverData } = useSelector((state) => state.driver);

  // Auto-expand wallet section if we're on a wallet page
  useEffect(() => {
    if (location.pathname.includes("/driver/wallet")) {
      setWalletOpen(true);
    }
  }, [location.pathname]);

  // Listen for pending rides count updates from DriverDashboard
  useEffect(() => {
    const handlePendingRidesUpdate = (e) => {
      setPendingRidesCount(e.detail?.count || 0);
    };
    window.addEventListener("pendingRidesUpdate", handlePendingRidesUpdate);
    return () => window.removeEventListener("pendingRidesUpdate", handlePendingRidesUpdate);
  }, []);

  // Check driver authentication exactly once when the layout mounts
  useEffect(() => {
    if (
      location.pathname === "/driver/login" ||
      location.pathname === "/driver/register"
    ) {
      return;
    }
    if (driverUser === null && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate("/driver/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAvailability = async () => {
    setLoading(true);
    dispatch(
      updateDriverAvailability({
        driverId: driverData._id,
        isAvailable: !driverData.isAvailable,
      }),
    );
    setLoading(false);
  };

  const handleLogout = () => {
    dispatch(logoutDriver());
    hasNavigated.current = true;
    navigate("/driver/login");
  };

  const isWalletActive = location.pathname.includes("/driver/wallet");

  const navLinkClass = (path) =>
    `flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:translate-x-1 ${
      location.pathname === path
        ? "bg-gradient-to-r from-amber-500/15 to-transparent text-amber-500 border-l-4 border-amber-500"
        : "text-slate-400 hover:bg-slate-800/40 hover:text-white border-l-4 border-transparent"
    }`;

  const walletSubLinkClass = (path) =>
    `flex items-center pl-10 pr-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
      location.pathname === path
        ? "bg-amber-500/10 text-amber-400 border-l-4 border-amber-400"
        : "text-slate-500 hover:bg-slate-800/40 hover:text-slate-300 border-l-4 border-transparent"
    }`;

  const mobileNavLinkClass = (path) =>
    `flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      location.pathname === path
        ? "bg-amber-500/10 text-amber-500 border-l-4 border-amber-500"
        : "text-slate-300 hover:bg-slate-800 hover:text-white"
    }`;

  return (
    <div className="h-screen overflow-hidden bg-slate-50 flex flex-col font-sans">
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 shadow-xl transition-all duration-300 animate-fadeIn">
          <div className="container mx-auto px-4 py-3">
            <ul className="space-y-1">
              <li>
                <Link
                  to="/driver/dashboard"
                  className={mobileNavLinkClass("/driver/dashboard")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaHome className="mr-3 text-lg" /> Dashboard
                </Link>
              </li>

              {pendingRidesCount > 0 && (
                <li>
                  <Link
                    to="/driver/new-ride-requests"
                    className="flex items-center px-4 py-2.5 rounded-lg text-sm font-bold text-emerald-400 bg-emerald-500/10 border-l-4 border-emerald-500 animate-pulse"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaBell className="mr-3 text-lg" />
                    New Ride Requests
                    <span className="ml-auto bg-emerald-500 text-white text-xs font-extrabold px-2 py-0.5 rounded-full">
                      {pendingRidesCount}
                    </span>
                  </Link>
                </li>
              )}

              <li>
                <Link to="/driver/current-ride" className={mobileNavLinkClass("/driver/current-ride")} onClick={() => setIsMenuOpen(false)}>
                  <FaCarAlt className="mr-3 text-lg" /> Current Ride
                </Link>
              </li>
              <li>
                <Link to="/driver/ride-history" className={mobileNavLinkClass("/driver/ride-history")} onClick={() => setIsMenuOpen(false)}>
                  <FaHistory className="mr-3 text-lg" /> Ride History
                </Link>
              </li>
              <li>
                <Link to="/driver/profile" className={mobileNavLinkClass("/driver/profile")} onClick={() => setIsMenuOpen(false)}>
                  <FaUserAlt className="mr-3 text-lg" /> Profile
                </Link>
              </li>

              {/* Wallet â€” Mobile */}
              <li className="pt-1">
                <p className="px-4 pb-1 text-xs font-bold text-slate-600 uppercase tracking-widest">Wallet</p>
              </li>
              <li>
                <Link to="/driver/wallet/history" className={mobileNavLinkClass("/driver/wallet/history")} onClick={() => setIsMenuOpen(false)}>
                  <FaClipboardList className="mr-3 text-lg" /> Wallet History
                </Link>
              </li>
              <li>
                <Link to="/driver/wallet/withdraw" className={mobileNavLinkClass("/driver/wallet/withdraw")} onClick={() => setIsMenuOpen(false)}>
                  <FaMoneyBillWave className="mr-3 text-lg" /> Withdraw
                </Link>
              </li>
              <li>
                <Link to="/driver/wallet/commission" className={mobileNavLinkClass("/driver/wallet/commission")} onClick={() => setIsMenuOpen(false)}>
                  <FaPercentage className="mr-3 text-lg" /> Commission
                </Link>
              </li>

              <li>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
                >
                  <FaSignOutAlt className="mr-3 text-lg" /> Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 h-full overflow-hidden">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 bg-slate-900 border-r border-slate-800 text-slate-300 pt-20 flex-shrink-0 z-20 flex-col h-full">
          <div className="p-4 flex-1 flex flex-col justify-between overflow-y-auto">
            <ul className="space-y-1.5">
              {/* Dashboard */}
              <li>
                <Link to="/driver/dashboard" className={navLinkClass("/driver/dashboard")}>
                  <FaHome className="mr-3 text-lg" /> Dashboard
                </Link>
              </li>

              {/* New Ride Requests */}
              <li>
                <Link
                  to="/driver/new-ride-requests"
                  className={`flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:translate-x-1 border-l-4 ${
                    location.pathname === "/driver/new-ride-requests"
                      ? "border-emerald-500 bg-gradient-to-r from-emerald-500/20 to-transparent text-emerald-400"
                      : pendingRidesCount > 0
                      ? "border-emerald-500 bg-gradient-to-r from-emerald-500/20 to-transparent text-emerald-400"
                      : "border-transparent text-slate-500 hover:bg-slate-800/40 hover:text-slate-300"
                  }`}
                >
                  <span className="relative mr-3 flex-shrink-0">
                    <FaBell className="text-lg" />
                    {pendingRidesCount > 0 && (
                      <>
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                        </span>
                      </>
                    )}
                  </span>
                  New Ride Requests
                  {pendingRidesCount > 0 ? (
                    <span className="ml-auto bg-emerald-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full animate-bounce">
                      {pendingRidesCount}
                    </span>
                  ) : (
                    <span className="ml-auto text-[10px] text-slate-600 font-semibold">0</span>
                  )}
                </Link>
              </li>

              {/* Current Ride */}
              <li>
                <Link to="/driver/current-ride" className={navLinkClass("/driver/current-ride")}>
                  <FaCarAlt className="mr-3 text-lg" /> Current Ride
                </Link>
              </li>

              {/* Ride History */}
              <li>
                <Link to="/driver/ride-history" className={navLinkClass("/driver/ride-history")}>
                  <FaHistory className="mr-3 text-lg" /> Ride History
                </Link>
              </li>

              {/* Profile */}
              <li>
                <Link to="/driver/profile" className={navLinkClass("/driver/profile")}>
                  <FaUserAlt className="mr-3 text-lg" /> Profile
                </Link>
              </li>

              {/* â”€â”€ Wallet collapsible section â”€â”€ */}
              <li className="mt-2">
                <button
                  onClick={() => setWalletOpen((o) => !o)}
                  className={`flex items-center w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:translate-x-1 border-l-4 ${
                    isWalletActive
                      ? "bg-gradient-to-r from-amber-500/15 to-transparent text-amber-500 border-amber-500"
                      : "text-slate-400 hover:bg-slate-800/40 hover:text-white border-transparent"
                  }`}
                >
                  <FaWallet className="mr-3 text-lg" />
                  Wallet
                  <span className="ml-auto transition-transform duration-200">
                    {walletOpen
                      ? <FaChevronDown className="text-xs" />
                      : <FaChevronRight className="text-xs" />
                    }
                  </span>
                </button>

                {walletOpen && (
                  <ul className="mt-1 space-y-0.5">
                    <li>
                      <Link to="/driver/wallet/history" className={walletSubLinkClass("/driver/wallet/history")}>
                        <FaClipboardList className="mr-2 text-sm" /> Wallet History
                      </Link>
                    </li>
                    <li>
                      <Link to="/driver/wallet/withdraw" className={walletSubLinkClass("/driver/wallet/withdraw")}>
                        <FaMoneyBillWave className="mr-2 text-sm" /> Withdraw
                      </Link>
                    </li>
                    <li>
                      <Link to="/driver/wallet/commission" className={walletSubLinkClass("/driver/wallet/commission")}>
                        <FaPercentage className="mr-2 text-sm" /> Commission
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
            </ul>

            <div className="pt-4 border-t border-slate-800/80">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300"
              >
                <FaSignOutAlt className="mr-3 text-lg" /> Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-grow pt-28 pb-6 px-6 md:pb-8 md:px-8 bg-slate-50 overflow-y-auto h-full">
          <div className="max-w-6xl mx-auto space-y-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DriverLayout;
